import { useState } from 'react';
import {
  Avatar,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Divider,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SendRegular } from '@fluentui/react-icons';

import { TChangeV2 } from '../../models';
import { formatRelativeTime } from '../../utils';
import { useChangeV2Store } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  muted: { color: tokens.colorNeutralForeground3 },
  participant: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  activityItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  comment: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  commentComposer: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
});

type TProps = {
  change: TChangeV2;
  canComment: boolean;
};

export const ContextPanel = ({ change, canComment }: TProps) => {
  const classes = useStyles();
  const { addComment } = useChangeV2Store();
  const [draft, setDraft] = useState('');

  const participants = Array.from(
    new Set([change.initiator, change.changeManager, ...change.collaborators].filter(Boolean) as string[])
  );
  const recentActivity = [...change.auditEvents].slice(-5).reverse();

  const handleSend = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    addComment(change.id, trimmed);
    setDraft('');
  };

  return (
    <div className={classes.root}>
      <div className={classes.block}>
        <Body1Strong>Next action</Body1Strong>
        <Body1>{change.nextAction ?? 'None'}</Body1>
        {change.currentOwner ? <Caption1 className={classes.muted}>Owner: {change.currentOwner}</Caption1> : null}
      </div>

      <Divider />

      <div className={classes.block}>
        <Body1Strong>Blockers</Body1Strong>
        {change.blockers.length > 0 ? (
          change.blockers.map((blocker) => <Body1 key={blocker}>{blocker}</Body1>)
        ) : (
          <Caption1 className={classes.muted}>No blockers.</Caption1>
        )}
      </div>

      <Divider />

      <div className={classes.block}>
        <Body1Strong>Participants</Body1Strong>
        {participants.map((participant) => (
          <div key={participant} className={classes.participant}>
            <Avatar name={participant} size={24} color="colorful" />
            <Body1>{participant}</Body1>
          </div>
        ))}
      </div>

      <Divider />

      <div className={classes.block}>
        <Body1Strong>Recent activity</Body1Strong>
        {recentActivity.length > 0 ? (
          recentActivity.map((event) => (
            <div key={event.id} className={classes.activityItem}>
              <Body1>{event.description}</Body1>
              <Caption1 className={classes.muted}>
                {event.actor} · {formatRelativeTime(event.timestamp)}
              </Caption1>
            </div>
          ))
        ) : (
          <Caption1 className={classes.muted}>No activity yet.</Caption1>
        )}
      </div>

      <Divider />

      <div className={classes.block}>
        <Body1Strong>Comments</Body1Strong>
        {change.comments.length > 0 ? (
          change.comments.map((comment) => (
            <div key={comment.id} className={classes.comment}>
              <Caption1 className={classes.muted}>
                {comment.author} · {comment.authorRole}
              </Caption1>
              <Body1>{comment.body}</Body1>
            </div>
          ))
        ) : (
          <Caption1 className={classes.muted}>No comments yet.</Caption1>
        )}
        {canComment ? (
          <div className={classes.commentComposer}>
            <Textarea
              value={draft}
              onChange={(_, data) => setDraft(data.value)}
              placeholder="Add a comment"
              resize="vertical"
            />
            <Button appearance="primary" icon={<SendRegular />} disabled={!draft.trim()} onClick={handleSend}>
              Comment
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
